import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col, Spinner, Badge, Tabs, Tab } from 'react-bootstrap';
import { FaEdit, FaTrash, FaSearch, FaPlus, FaBook, FaArrowLeft, FaCheck, FaTimes, FaClock, FaStar, FaUsersCog, FaChalkboardTeacher, FaEye, FaUserGraduate } from 'react-icons/fa';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { validateOrganizationToken } from '../../../utils/urlSecurity';
import api from '../../../api/apiClient';
import { toast } from 'react-toastify';

const OrgCourses = () => {
  const navigate = useNavigate();
  const { orgId: secureToken } = useParams();
  const location = useLocation();
  const organizationName = location.state?.organizationName || 'Organization';
  
  const [orgId, setOrgId] = useState(null);
  const [tokenValid, setTokenValid] = useState(false);
  const [orgCourses, setOrgCourses] = useState([]);
  const [allCourses, setAllCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingCourse, setEditingCourse] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [courseToDelete, setCourseToDelete] = useState(null);
  const [selectedCourseId, setSelectedCourseId] = useState('');
  const [formData, setFormData] = useState({ status: 1 });
  const [formErrors, setFormErrors] = useState({});

  // Group enrollment state
  const [showGroupEnrollModal, setShowGroupEnrollModal] = useState(false);
  const [groupEnrollCourse, setGroupEnrollCourse] = useState(null);
  const [enrollmentType, setEnrollmentType] = useState('group');
  const [orgGroups, setOrgGroups] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState('');
  const [groupPreview, setGroupPreview] = useState({ toEnroll: [], alreadyEnrolled: [] });
  const [groupPreviewLoading, setGroupPreviewLoading] = useState(false);
  const [groupEnrollSaving, setGroupEnrollSaving] = useState(false);

  // Individual learner enrollment state
  const [availableLearners, setAvailableLearners] = useState([]);
  const [selectedLearnerIds, setSelectedLearnerIds] = useState([]);
  const [learnerSearch, setLearnerSearch] = useState('');
  const [individualLoading, setIndividualLoading] = useState(false);

  // Enrolled users roster state
  const [showRosterModal, setShowRosterModal] = useState(false);
  const [rosterCourse, setRosterCourse] = useState(null);
  const [rosterTab, setRosterTab] = useState('learners');
  const [enrolledLearners, setEnrolledLearners] = useState([]);
  const [assignedInstructors, setAssignedInstructors] = useState([]);
  const [allAssignedInstructorIds, setAllAssignedInstructorIds] = useState([]);
  const [rosterLoading, setRosterLoading] = useState(false);
  const [rosterActionLoading, setRosterActionLoading] = useState(null);

  const validateToken = () => {
    const validation = validateOrganizationToken(secureToken);
    if (!validation.valid) {
      toast.error(validation.expired ? 'Navigation token has expired.' : 'Invalid navigation token.');
      navigate('/admin/organizations');
      return false;
    }
    setOrgId(parseInt(validation.orgId));
    setTokenValid(true);
    return true;
  };

  const STATUS_LABELS = {
    0: { label: 'Inactive', color: 'bg-gray-100 text-gray-700', icon: FaTimes },
    1: { label: 'Active', color: 'bg-green-100 text-green-800', icon: FaCheck },
    2: { label: 'Pending', color: 'bg-amber-100 text-amber-800', icon: FaClock },
    3: { label: 'Completed', color: 'bg-blue-100 text-blue-800', icon: FaStar }
  };

  const fetchOrgCourses = async () => {
    try {
      setLoading(true);
      const response = await api.organizations.getCourses(orgId, true);
      const coursesData = response?.data?.data || response?.data || [];
      setOrgCourses(Array.isArray(coursesData) ? coursesData : []);
    } catch (error) {
      console.error('Error fetching organization courses:', error);
      toast.error('Failed to load courses');
      setOrgCourses([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllCourses = async () => {
    try {
      const response = await api.courses.getAll(true);
      const courses = (response.data.success ? response.data.data : response.data) || [];
      const assignedIds = orgCourses.map(oc => oc?.course_id).filter(Boolean);
      const available = courses.filter(c => !assignedIds.includes(c.id) && !c.deleted);
      setAllCourses(available);
    } catch (error) {
      console.error('Error fetching all courses:', error);
      toast.error('Failed to load available courses');
    }
  };

  useEffect(() => { validateToken(); }, [secureToken]);
  useEffect(() => { if (orgId && tokenValid) fetchOrgCourses(); }, [orgId, tokenValid]);
  useEffect(() => { if (orgCourses.length >= 0) fetchAllCourses(); }, [orgCourses]);

  const handleSearch = (e) => setSearchTerm(e.target.value);
  const handleFilterChange = (e) => setFilterStatus(e.target.value);

  const validateForm = () => {
    const errors = {};
    if (!selectedCourseId) errors.courseId = 'Please select a course';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleShowEditModal = (orgCourse) => {
    setEditingCourse(orgCourse);
    setFormData({ status: orgCourse.status });
    setFormErrors({});
    setShowModal(true);
  };

  const handleShowAddModal = () => {
    setSelectedCourseId('');
    setFormData({ status: 1 });
    setFormErrors({});
    setShowAddModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setShowAddModal(false);
    setEditingCourse(null);
    setSelectedCourseId('');
    setFormData({ status: 1 });
    setFormErrors({});
  };

    const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setCourseToDelete(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'courseId') setSelectedCourseId(value);
    else setFormData(prev => ({ ...prev, status: parseInt(value) }));
    if (formErrors[name]) setFormErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleUpdateCourse = async (e) => {
    e.preventDefault();
    if (!editingCourse) return;
    setModalLoading(true);
    try {
      await api.organizations.updateCourse(orgId, editingCourse.course_id, formData);
      toast.success('Course updated successfully!');
      handleCloseModal();
      fetchOrgCourses();
    } catch (error) {
      toast.error('Failed to update course');
    } finally {
      setModalLoading(false);
    }
  };

  const handleAddCourse = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setModalLoading(true);
    try {
      await api.organizations.addCourse(orgId, { course_id: selectedCourseId, status: formData.status });
      toast.success('Course assigned successfully!');
      handleCloseModal();
      fetchOrgCourses();
    } catch (error) {
      toast.error('Failed to assign course');
    } finally {
      setModalLoading(false);
    }
  };

  const handleDeleteClick = (orgCourse) => {
    setCourseToDelete(orgCourse);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!courseToDelete) return;
    setDeleteLoading(courseToDelete.id);
    try {
      await api.organizations.removeCourse(orgId, courseToDelete.course_id);
      toast.success('Course removed successfully!');
      setShowDeleteModal(false);
      setCourseToDelete(null);
      fetchOrgCourses();
    } catch (error) {
      toast.error('Failed to remove course');
    } finally {
      setDeleteLoading(null);
    }
  };

  const handleRestoreCourse = async (orgCourse) => {
    try {
      await api.organizations.restoreCourse(orgId, orgCourse.course_id);
      toast.success('Course restored successfully!');
      fetchOrgCourses();
    } catch (error) {
      toast.error('Failed to restore course');
    }
  };

  const handleOpenGroupEnroll = async (orgCourse) => {
    setGroupEnrollCourse(orgCourse);
    setEnrollmentType('group');
    setSelectedGroupId('');
    setGroupPreview({ toEnroll: [], alreadyEnrolled: [] });
    setAvailableLearners([]);
    setSelectedLearnerIds([]);
    setLearnerSearch('');
    try {
      const response = await api.groups.list();
      const allGroups = Array.isArray(response.data?.data) ? response.data.data : [];
      setOrgGroups(allGroups.filter((g) => parseInt(g.organization_id) === parseInt(orgId)));
    } catch (error) {
      toast.error('Failed to load groups: ' + (error.response?.data?.message || error.message));
    }
    setShowGroupEnrollModal(true);
  };

  const handleEnrollmentTypeChange = async (type) => {
    setEnrollmentType(type);
    setSelectedGroupId('');
    setGroupPreview({ toEnroll: [], alreadyEnrolled: [] });
    setSelectedLearnerIds([]);
    setLearnerSearch('');

    if (type === 'individual' && groupEnrollCourse) {
      try {
        setIndividualLoading(true);
        const response = await api.enrollments.getOrgAvailableLearners(groupEnrollCourse.course_id, orgId);
        setAvailableLearners(Array.isArray(response.data?.data) ? response.data.data : []);
      } catch (error) {
        toast.error('Failed to load learners: ' + (error.response?.data?.message || error.message));
      } finally {
        setIndividualLoading(false);
      }
    }
  };

  const handleGroupSelect = async (e) => {
    const groupId = e.target.value;
    setSelectedGroupId(groupId);
    setGroupPreview({ toEnroll: [], alreadyEnrolled: [] });
    if (!groupId || !groupEnrollCourse) return;

    const courseId = groupEnrollCourse.course_id;
    try {
      setGroupPreviewLoading(true);
      const response = await api.enrollments.getOrgGroupPreview(courseId, groupId, orgId);
      if (response.data?.success) {
        setGroupPreview(response.data.data);
      }
    } catch (error) {
      toast.error('Failed to load preview: ' + (error.response?.data?.message || error.message));
    } finally {
      setGroupPreviewLoading(false);
    }
  };

  const handleGroupEnrollSave = async () => {
    if (!groupEnrollCourse) return;

    if (enrollmentType === 'group') {
      if (!selectedGroupId) return;
      try {
        setGroupEnrollSaving(true);
        const response = await api.enrollments.enrollOrgGroup(
          groupEnrollCourse.course_id,
          selectedGroupId,
          orgId
        );
        const { enrolled, skipped } = response.data;
        toast.success(`${enrolled} user(s) enrolled. ${skipped} already enrolled (skipped).`);
        setShowGroupEnrollModal(false);
      } catch (error) {
        toast.error('Failed to enroll group: ' + (error.response?.data?.message || error.message));
      } finally {
        setGroupEnrollSaving(false);
      }
      return;
    }

    if (selectedLearnerIds.length === 0) {
      toast.error('Please select at least one learner.');
      return;
    }

    try {
      setGroupEnrollSaving(true);
      const response = await api.enrollments.enrollOrgIndividuals(
        groupEnrollCourse.course_id,
        selectedLearnerIds,
        orgId
      );
      const { enrolled, skipped } = response.data;
      toast.success(`${enrolled} learner(s) enrolled. ${skipped} skipped (already enrolled or invalid).`);
      setShowGroupEnrollModal(false);
    } catch (error) {
      toast.error('Failed to enroll learners: ' + (error.response?.data?.message || error.message));
    } finally {
      setGroupEnrollSaving(false);
    }
  };

  const handleToggleLearner = (learnerId) => {
    setSelectedLearnerIds((prev) =>
      prev.includes(learnerId) ? prev.filter((id) => id !== learnerId) : [...prev, learnerId]
    );
  };

  const filteredAvailableLearners = availableLearners.filter((learner) => {
    if (!learnerSearch.trim()) return true;
    const q = learnerSearch.toLowerCase();
    return (
      `${learner.first_name} ${learner.last_name}`.toLowerCase().includes(q) ||
      learner.email?.toLowerCase().includes(q)
    );
  });

  const fetchRosterData = async (orgCourse) => {
    const courseId = orgCourse.course_id;
    setRosterLoading(true);
    try {
      const [enrollmentsRes, instructorsRes] = await Promise.all([
        api.enrollments.getByCourse(courseId, { orgId }),
        api.courses.getCourseInstructors(courseId, orgId)
      ]);

      const enrollments = enrollmentsRes.data?.data || enrollmentsRes.data || [];
      setEnrolledLearners(
        Array.isArray(enrollments)
          ? enrollments.filter((enrollment) => !enrollment.deleted)
          : []
      );

      const instructorPayload = instructorsRes.data?.data || {};
      const instructors = Array.isArray(instructorPayload.instructors) ? instructorPayload.instructors : [];
      const assignedIds = Array.isArray(instructorPayload.assigned_ids) ? instructorPayload.assigned_ids : [];
      setAllAssignedInstructorIds(assignedIds);
      setAssignedInstructors(instructors.filter((inst) => assignedIds.includes(inst.id)));
    } catch (error) {
      toast.error('Failed to load enrolled users: ' + (error.response?.data?.message || error.message));
      setEnrolledLearners([]);
      setAssignedInstructors([]);
      setAllAssignedInstructorIds([]);
    } finally {
      setRosterLoading(false);
    }
  };

  const handleOpenRosterModal = async (orgCourse) => {
    setRosterCourse(orgCourse);
    setRosterTab('learners');
    setShowRosterModal(true);
    await fetchRosterData(orgCourse);
  };

  const handleCloseRosterModal = () => {
    if (rosterActionLoading) return;
    setShowRosterModal(false);
    setRosterCourse(null);
    setEnrolledLearners([]);
    setAssignedInstructors([]);
    setAllAssignedInstructorIds([]);
  };

  const handleDeEnrollLearner = async (enrollment) => {
    const learnerName = enrollment.User
      ? `${enrollment.User.first_name || ''} ${enrollment.User.last_name || ''}`.trim()
      : 'this learner';

    // eslint-disable-next-line no-alert
    const confirmed = window.confirm(`Unenroll ${learnerName} from this course?`);
    if (!confirmed) return;

    try {
      setRosterActionLoading(`learner-${enrollment.id}`);
      await api.enrollments.delete(enrollment.id);
      toast.success('Learner unenrolled successfully.');
      if (rosterCourse) {
        await fetchRosterData(rosterCourse);
      }
    } catch (error) {
      toast.error('Failed to unenroll learner: ' + (error.response?.data?.message || error.message));
    } finally {
      setRosterActionLoading(null);
    }
  };

  const handleRemoveInstructor = async (instructor) => {
    const instructorName = `${instructor.first_name || ''} ${instructor.last_name || ''}`.trim() || instructor.email;

    // eslint-disable-next-line no-alert
    const confirmed = window.confirm(`Remove ${instructorName} as instructor for this course?`);
    if (!confirmed || !rosterCourse) return;

    try {
      setRosterActionLoading(`instructor-${instructor.id}`);
      const newIds = allAssignedInstructorIds.filter((id) => id !== instructor.id);
      await api.courses.setCourseInstructors(rosterCourse.course_id, newIds);
      toast.success('Instructor removed successfully.');
      await fetchRosterData(rosterCourse);
    } catch (error) {
      toast.error('Failed to remove instructor: ' + (error.response?.data?.message || error.message));
    } finally {
      setRosterActionLoading(null);
    }
  };

  // --- Assign Instructor state ---
  const [showInstructorModal, setShowInstructorModal] = useState(false);
  const [instructorCourse, setInstructorCourse] = useState(null);
  const [allInstructors, setAllInstructors] = useState([]);
  const [assignedInstructorIds, setAssignedInstructorIds] = useState([]);
  const [instructorModalLoading, setInstructorModalLoading] = useState(false);
  const [instructorSaving, setInstructorSaving] = useState(false);
  const [instructorSearch, setInstructorSearch] = useState('');

  const handleOpenInstructorModal = async (orgCourse) => {
    setInstructorCourse(orgCourse);
    setInstructorSearch('');
    setShowInstructorModal(true);
    setInstructorModalLoading(true);
    try {
      const res = await api.courses.getCourseInstructors(orgCourse.course_id, orgId);
      if (res.data?.success) {
        setAllInstructors(res.data.data.instructors || []);
        setAssignedInstructorIds(res.data.data.assigned_ids || []);
      }
    } catch (err) {
      toast.error('Failed to load instructors: ' + (err.response?.data?.message || err.message));
    } finally {
      setInstructorModalLoading(false);
    }
  };

  const handleToggleInstructor = (id) => {
    setAssignedInstructorIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleSaveInstructors = async () => {
    if (!instructorCourse) return;
    setInstructorSaving(true);
    try {
      await api.courses.setCourseInstructors(instructorCourse.course_id, assignedInstructorIds);
      toast.success('Instructors updated successfully');
      setShowInstructorModal(false);
    } catch (err) {
      toast.error('Failed to save instructors: ' + (err.response?.data?.message || err.message));
    } finally {
      setInstructorSaving(false);
    }
  };

  const filteredInstructorList = allInstructors.filter((u) => {
    if (!instructorSearch.trim()) return true;
    const q = instructorSearch.toLowerCase();
    return (
      `${u.first_name} ${u.last_name}`.toLowerCase().includes(q) ||
      u.email?.toLowerCase().includes(q)
    );
  });

  const handleGoBack = () => navigate('/admin/organizations');

  const filteredCourses = Array.isArray(orgCourses) ? orgCourses.filter(orgCourse => {
    const course = orgCourse.course || orgCourse;
    const search = searchTerm.toLowerCase();
    const matchesSearch = (course?.name || course?.title || '').toLowerCase().includes(search) ||
                         (course?.description || '').toLowerCase().includes(search);
    let matchesStatus = true;
    switch (filterStatus) {
      case 'active': matchesStatus = orgCourse.status === 1 && !orgCourse.deleted; break;
      case 'inactive': matchesStatus = orgCourse.status === 0 && !orgCourse.deleted; break;
      case 'deleted': matchesStatus = !!orgCourse.deleted; break;
      default: matchesStatus = !orgCourse.deleted; break;
    }
    return matchesSearch && matchesStatus;
  }) : [];

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-96 p-4">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-600 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="p-3 sm:p-4 md:p-6 bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 min-h-screen">

      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <div className="flex items-center gap-4">
          <button
            onClick={handleGoBack}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-purple-300 text-purple-700 rounded-lg shadow-md hover:bg-purple-50 hover:shadow-lg transition-all duration-200 text-sm font-medium"
          >
            <FaArrowLeft className="w-4 h-4" /> Back
          </button>
          <h5 className="text-xl sm:text-2xl md:text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600 mb-0">
            {organizationName}'s Courses
          </h5>
        </div>
      </div>

      {/* Search + Filter + Add */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <div className="lg:col-span-2">
          <div className="flex shadow-sm rounded-lg sm:rounded-xl overflow-hidden bg-white">
            <div className="flex items-center px-3 bg-white border-r border-gray-200">
              <FaSearch className="w-4 h-4 text-purple-600" />
            </div>
            <input
              type="text"
              placeholder="Search by course name or description..."
              value={searchTerm}
              onChange={handleSearch}
              className="w-full px-3 py-2 border-0 focus:ring-2 focus:ring-purple-500 text-sm sm:text-base outline-none"
            />
          </div>
        </div>
        <select
          value={filterStatus}
          onChange={handleFilterChange}
          className="rounded-lg sm:rounded-xl shadow-sm border-purple-200 focus:ring-2 focus:ring-purple-500 text-sm form-select"
        >
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
          <option value="deleted">Deleted</option>
        </select>
        <button
          onClick={handleShowAddModal}
          className="w-full border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2  custom-hamburger custom-hamburger-primary p-2"
        >
          <FaPlus className="w-4 h-4" />
          <span className="whitespace-nowrap">Assign Course</span>
        </button>
      </div>

      {/* No Data */}
      {filteredCourses.length === 0 ? (
        <div className="text-center py-10 rounded-xl sm:rounded-2xl bg-white/80 backdrop-blur shadow-md border border-purple-100">
          <p className="text-lg font-medium text-purple-700">
            {searchTerm || filterStatus !== 'all'
              ? 'No courses found matching your criteria.'
              : 'No courses assigned yet. Click "Assign Course" to add one.'
            }
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto -mx-3 sm:mx-0 rounded-xl sm:rounded-2xl shadow-lg bg-white">
          <table className="m-0 min-w-max text-xs sm:text-sm table table-bordered table-hover">
            <thead className="bg-gradient-to-r from-purple-600 to-pink-600 text-white">
              <tr>
                <th className="py-3 px-3 text-left font-bold">Course</th>
                <th className="py-3 px-3 text-left font-bold">Status</th>
                <th className="py-3 px-3 text-left font-bold">Assigned Date</th>
                <th className="py-3 px-3 text-left font-bold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredCourses.map((orgCourse) => {
                const course = orgCourse.course || orgCourse;
                const statusInfo = STATUS_LABELS[orgCourse.status] || STATUS_LABELS[0];
                const StatusIcon = statusInfo.icon;

                return (
                  <tr key={orgCourse.id} className={`border-b border-purple-100 hover:bg-purple-50/50 transition-all ${orgCourse.deleted ? 'bg-red-50' : ''}`}>
                    <td className="py-2 px-3">
                      <div className="flex items-center gap-3">
                        {/* <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white font-bold shadow-sm w-fit">
                          <FaBook className="w-5 h-5" />
                        </div> */}
                        <div>
                          <div className="font-semibold text-gray-800">{course.name || course.title}</div>
                          {course.duration && <div className="text-xs text-gray-500">{course.duration} mins duration</div>}
                        </div>
                      </div>
                    </td>
                    <td className="py-2 px-3 text-left">
                      <div className="flex flex-col items-left gap-1">
                        <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${statusInfo.color} w-fit`}>
                          <StatusIcon className="w-4 h-4" />
                          {statusInfo.label}
                        </span>
                        {orgCourse.deleted && <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded">Deleted</span>}
                      </div>
                    </td>
                    <td className="py-2 px-3 text-left text-gray-800">
                      {orgCourse.createdAt ? new Date(orgCourse.createdAt).toLocaleDateString() : '-'}
                    </td>
                    <td className="py-2 px-3">
                      <div className="flex gap-2 justify-left">
                        {!orgCourse.deleted ? (
                          <>
                            <button
                              onClick={() => handleShowEditModal(orgCourse)}
                              className="p-2 text-purple-600 hover:bg-purple-50 rounded-lg transition"
                              title="Edit"
                            >
                              <FaEdit className="w-5 h-5" />
                            </button>
                            <button
                              onClick={() => handleOpenGroupEnroll(orgCourse)}
                              className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition"
                              title="Enroll Users"
                            >
                              <FaUsersCog className="w-5 h-5" />
                            </button>
                            <button
                              onClick={() => handleOpenRosterModal(orgCourse)}
                              className="p-2 text-purple-600 hover:bg-purple-50 rounded-lg transition"
                              title="View Enrolled Learners & Instructors"
                            >
                              <FaEye className="w-5 h-5" />
                            </button>
                            <button
                              onClick={() => handleOpenInstructorModal(orgCourse)}
                              className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-lg transition"
                              title="Assign Instructor"
                            >
                              <FaChalkboardTeacher className="w-5 h-5" />
                            </button>
                            <button
                              onClick={() => handleDeleteClick(orgCourse)}
                              disabled={deleteLoading === orgCourse.id}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition disabled:opacity-50"
                              title="Remove"
                            >
                              {deleteLoading === orgCourse.id ? (
                                <div className="w-5 h-5 border-2 border-red-600 border-t-transparent rounded-full animate-spin"></div>
                              ) : (
                                <FaTrash className="w-5 h-5" />
                              )}
                            </button>
                          </>
                        ) : (
                          <button
                            onClick={() => handleRestoreCourse(orgCourse)}
                            className="p-2  text-green-600 hover:bg-green-50 rounded-lg transition"
                            title="Restore"
                          >
                            <FaCheck className="w-5 h-5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Edit Modal */}
      <Modal show={showModal} onHide={handleCloseModal} centered>
        <Modal.Header closeButton closeVariant="white" className="bg-gradient-to-r from-purple-600 to-pink-600 text-white border-0 py-3 px-4 sm:px-4 sm:py-4 modal-header">
          <Modal.Title className="font-bold text-xl text-white">
            Edit Course Status
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="bg-gray-50 p-3 sm:p-5">
            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Course</label>
              <div className="px-4 py-3 bg-gray-100 rounded-lg text-gray-800 font-medium">
                {editingCourse?.course?.name || editingCourse?.course?.title}
              </div>
            </div>
            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Status</label>
              <select
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                className="w-full px-4 py-2 rounded-lg border border-purple-200 focus:ring-2 focus:ring-purple-500 outline-none"
              >
                <option value={0}>Inactive</option>
                <option value={1}>Active</option>
              </select>
            </div>
            <Modal.Footer className="bg-gray-100 border-0 rounded-b-2xl p-4">
              <Row className="w-100 g-3">
                <Col md={6}>
                  <Button bsPrefix="custom-hamburger" onClick={handleCloseModal} disabled={modalLoading} className="w-full border-1 bg-gray-100 hover:bg-gray-200 text-gray-700 border-2 border-gray-300 hover:border-gray-500 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2">
                    Cancel
                  </Button>
                </Col>
                <Col md={6}>
                  <Button bsPrefix="custom-hamburger" onClick={handleUpdateCourse} disabled={modalLoading} className="w-full border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2">
                    {modalLoading ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Updating...
                      </>
                    ) : (
                      'Update Course'
                    )}
                  </Button>
                </Col>
              </Row>
            </Modal.Footer>
        </Modal.Body>
      </Modal>

      {/* Add Modal */}
      <Modal show={showAddModal} onHide={handleCloseModal} centered>
        <Modal.Header closeButton closeVariant="white" className="bg-gradient-to-r from-purple-600 to-pink-600 text-white border-0 py-3 px-4 sm:px-4 sm:py-4 modal-header">
          <Modal.Title className="font-bold text-xl text-white">
            Assign New Course
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="bg-gray-50 p-3 sm:p-5">
            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Select Course <span className="text-red-500">*</span></label>
              <select
                name="courseId"
                value={selectedCourseId}
                onChange={handleInputChange}
                className={`w-full px-4 py-2 rounded-lg border ${formErrors.courseId ? 'border-red-500' : 'border-purple-200'} focus:ring-2 focus:ring-purple-500 outline-none`}
              >
                <option value="">Choose a course...</option>
                {allCourses.map(c => (
                  <option key={c.id} value={c.id}>
                    {c.name || c.title} {c.duration && `(${c.duration} mins)`}
                  </option>
                ))}
              </select>
              {formErrors.courseId && <p className="text-red-500 text-xs mt-1">{formErrors.courseId}</p>}
            </div>
            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Initial Status</label>
              <select
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                className="w-full px-4 py-2 rounded-lg border border-purple-200 focus:ring-2 focus:ring-purple-500 outline-none"
              >
                <option value={0}>Inactive</option>
                <option value={1}>Active</option>
              </select>
            </div>
            <Modal.Footer className="bg-gray-100 border-0 rounded-b-2xl p-4">
              <Row className="w-100 g-3">
                <Col md={6}>
                  <Button bsPrefix="custom-hamburger" onClick={handleCloseModal} disabled={modalLoading} className="w-full border-1 bg-gray-100 hover:bg-gray-200 text-gray-700 border-2 border-gray-300 hover:border-gray-500 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2">
                    Cancel
                  </Button>
                </Col>
                <Col md={6}>
                  <Button bsPrefix="custom-hamburger" onClick={handleAddCourse} disabled={modalLoading} className="w-full border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2">
                    {modalLoading ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Assigning...
                      </>
                    ) : (
                      'Assign Course'
                    )}
                  </Button>
                </Col>
              </Row>
            </Modal.Footer>
        </Modal.Body>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={handleDeleteCancel} centered>
        <Modal.Header closeButton closeVariant="white" className="bg-gradient-to-r from-purple-600 to-pink-600 text-white border-0 py-3 px-4 sm:px-4 sm:py-4 modal-header">
          <Modal.Title className="font-bold text-xl text-white">
            Confirm Delete
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center py-10 bg-gray-50">
          <div className="mb-4">
            <FaTrash size={60} className="text-red-500 mx-auto" />
          </div>
          <h5 className="font-bold text-gray-800 mb-3">Remove Course</h5>
          <p className="text-gray-600">
            Are you sure you want to remove <strong>{courseToDelete?.course?.name || courseToDelete?.course?.title}</strong> from this organization?
          </p>
          <p className="text-sm text-gray-500">This is a soft delete. The course can be restored later.</p>
        </Modal.Body>
        <Modal.Footer className="bg-gray-100 border-0 rounded-b-2xl p-4">
          <Row className="w-100 g-3">
            <Col md={6}>
              <Button bsPrefix="custom-hamburger" onClick={handleDeleteCancel} className="w-full border-1 bg-gray-100 hover:bg-gray-200 text-gray-700 border-2 border-gray-300 hover:border-gray-500 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2">
                Cancel
              </Button>
            </Col>
            <Col md={6}>
              <Button bsPrefix="custom-hamburger" onClick={handleDeleteConfirm} disabled={deleteLoading} className="w-full border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2">
                {deleteLoading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Removing...
                  </>
                ) : (
                  'Remove Course'
                )}
              </Button>
            </Col>
          </Row>
        </Modal.Footer>
      </Modal>
      {/* Enrollment Modal */}
      <Modal show={showGroupEnrollModal} onHide={() => !groupEnrollSaving && setShowGroupEnrollModal(false)} size="lg" centered backdrop="static">
        <Modal.Header closeButton closeVariant="white" className="bg-gradient-to-r from-blue-500 to-purple-600 text-white border-0 py-3 px-4 modal-header">
          <Modal.Title className="font-bold text-lg text-white">
            <FaUsersCog className="inline me-2" />
            Enroll Users — {groupEnrollCourse?.course?.name || groupEnrollCourse?.course?.title}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="bg-gray-50 p-4">
          <Form.Group className="mb-4">
            <Form.Label className="font-semibold text-gray-700 text-sm">Enrollment Type</Form.Label>
            <div className="d-flex flex-wrap gap-3">
              <Form.Check
                type="radio"
                id="enroll-type-group"
                name="enrollmentType"
                label="Group"
                checked={enrollmentType === 'group'}
                onChange={() => handleEnrollmentTypeChange('group')}
                disabled={groupEnrollSaving}
              />
              <Form.Check
                type="radio"
                id="enroll-type-individual"
                name="enrollmentType"
                label="Individual Learner"
                checked={enrollmentType === 'individual'}
                onChange={() => handleEnrollmentTypeChange('individual')}
                disabled={groupEnrollSaving}
              />
            </div>
          </Form.Group>

          {enrollmentType === 'group' ? (
            <>
              <Form.Group className="mb-4">
                <Form.Label className="font-semibold text-gray-700 text-sm">
                  Select Group <span className="text-danger">*</span>
                </Form.Label>
                <Form.Select
                  value={selectedGroupId}
                  onChange={handleGroupSelect}
                  disabled={groupPreviewLoading || groupEnrollSaving}
                  className="rounded-lg border-purple-200 focus:ring-purple-500 text-sm"
                >
                  <option value="">Choose a group linked to this organization...</option>
                  {orgGroups.map((g) => (
                    <option key={g.id} value={g.id}>{g.name}</option>
                  ))}
                </Form.Select>
                {orgGroups.length === 0 && (
                  <Form.Text className="text-muted text-xs">
                    No groups linked to this organization. Create a group and link it via Manage Group.
                  </Form.Text>
                )}
              </Form.Group>

              {groupPreviewLoading ? (
                <div className="text-center py-8">
                  <Spinner animation="border" className="text-purple-600" />
                </div>
              ) : selectedGroupId ? (
                <div className="rounded-xl overflow-hidden border border-purple-100">
                  <div className="px-3 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white text-sm font-semibold d-flex justify-content-between align-items-center">
                    <span><FaCheck className="inline me-1" /> Will be enrolled</span>
                    <span className="bg-white/30 rounded-full px-2 py-0.5 text-xs ms-2">{groupPreview.toEnroll.length}</span>
                  </div>
                  <div className="bg-white px-3 py-2 max-h-44 overflow-y-auto">
                    {groupPreview.toEnroll.length === 0 ? (
                      <p className="text-gray-400 text-xs py-2 text-center">All group members are already enrolled.</p>
                    ) : (
                      groupPreview.toEnroll.map((u) => (
                        <div key={u.id} className="d-flex align-items-center gap-2 py-1 border-bottom">
                          <FaCheck className="text-green-500 shrink-0" style={{ width: 12 }} />
                          <span className="text-sm text-gray-800">{u.first_name} {u.last_name}</span>
                          <span className="text-xs text-gray-500">({u.email})</span>
                        </div>
                      ))
                    )}
                  </div>
                  {groupPreview.alreadyEnrolled.length > 0 && (
                    <>
                      <div className="px-3 py-2 bg-gray-200 text-gray-600 text-sm font-semibold d-flex justify-content-between align-items-center">
                        <span><FaTimes className="inline me-1" /> Already enrolled (skipped)</span>
                        <span className="bg-gray-400 text-white rounded-full px-2 py-0.5 text-xs ms-2">{groupPreview.alreadyEnrolled.length}</span>
                      </div>
                      <div className="bg-gray-50 px-3 py-2 max-h-32 overflow-y-auto">
                        {groupPreview.alreadyEnrolled.map((u) => (
                          <div key={u.id} className="d-flex align-items-center gap-2 py-1 border-bottom">
                            <FaTimes className="text-gray-400 shrink-0" style={{ width: 12 }} />
                            <span className="text-sm text-gray-500 text-decoration-line-through">{u.first_name} {u.last_name}</span>
                            <span className="text-xs text-gray-400">({u.email})</span>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              ) : null}
            </>
          ) : (
            <>
              <div className="mb-3">
                <div className="flex shadow-sm rounded-lg overflow-hidden bg-white border border-purple-200">
                  <div className="flex items-center px-3 bg-white">
                    <FaSearch className="w-4 h-4 text-purple-500" />
                  </div>
                  <input
                    type="text"
                    placeholder="Search learners by name or email..."
                    value={learnerSearch}
                    onChange={(e) => setLearnerSearch(e.target.value)}
                    className="w-full px-3 py-2 border-0 outline-none text-sm"
                    aria-label="Search learners"
                  />
                </div>
              </div>

              {individualLoading ? (
                <div className="text-center py-8">
                  <Spinner animation="border" className="text-purple-600" />
                </div>
              ) : filteredAvailableLearners.length === 0 ? (
                <p className="text-center text-sm text-gray-500 py-6">
                  {availableLearners.length === 0
                    ? 'No learners available for enrollment. All organization learners may already be enrolled.'
                    : 'No learners match your search.'}
                </p>
              ) : (
                <div className="rounded-xl border border-purple-100 bg-white max-h-80 overflow-y-auto p-2">
                  {filteredAvailableLearners.map((learner) => {
                    const isSelected = selectedLearnerIds.includes(learner.id);
                    return (
                      <div
                        key={learner.id}
                        role="button"
                        tabIndex={0}
                        onClick={() => handleToggleLearner(learner.id)}
                        onKeyDown={(e) => e.key === 'Enter' && handleToggleLearner(learner.id)}
                        aria-label={`Select learner ${learner.first_name} ${learner.last_name}`}
                        className={`d-flex align-items-center gap-3 p-2 rounded-lg mb-1 cursor-pointer transition-all ${
                          isSelected ? 'bg-purple-50 border border-purple-300' : 'hover:bg-gray-50 border border-transparent'
                        }`}
                      >
                        <Form.Check
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleToggleLearner(learner.id)}
                          onClick={(e) => e.stopPropagation()}
                          aria-label={`Enroll ${learner.first_name} ${learner.last_name}`}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-gray-800">{learner.first_name} {learner.last_name}</div>
                          <div className="text-xs text-gray-500">{learner.email}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {selectedLearnerIds.length > 0 && (
                <p className="text-sm text-purple-700 mt-2 mb-0">
                  {selectedLearnerIds.length} learner(s) selected for enrollment.
                </p>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer className="bg-gray-100 border-0 p-3">
          <Row className="w-100 g-3">
            <Col md={6}>
              <Button
                bsPrefix="custom-hamburger"
                onClick={() => setShowGroupEnrollModal(false)}
                disabled={groupEnrollSaving}
                className="w-full border-1 bg-gray-100 hover:bg-gray-200 text-gray-700 border-2 border-gray-300 hover:border-gray-500 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2"
              >
                Cancel
              </Button>
            </Col>
            <Col md={6}>
              <Button
                bsPrefix="custom-hamburger"
                onClick={handleGroupEnrollSave}
                disabled={
                  groupEnrollSaving ||
                  (enrollmentType === 'group'
                    ? !selectedGroupId || groupPreview.toEnroll.length === 0
                    : selectedLearnerIds.length === 0)
                }
                className="w-full border-1 border-purple-600 bg-white text-purple-600 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2 disabled:opacity-50"
              >
                {groupEnrollSaving ? (
                  <><Spinner animation="border" size="sm" className="me-2" /> Enrolling...</>
                ) : enrollmentType === 'group' ? (
                  <><FaUsersCog className="w-4 h-4" /> Enroll {groupPreview.toEnroll.length} User(s)</>
                ) : (
                  <><FaUsersCog className="w-4 h-4" /> Enroll {selectedLearnerIds.length} Learner(s)</>
                )}
              </Button>
            </Col>
          </Row>
        </Modal.Footer>
      </Modal>

      {/* Enrolled Users Roster Modal */}
      <Modal
        show={showRosterModal}
        onHide={handleCloseRosterModal}
        size="lg"
        centered
        backdrop="static"
      >
        <Modal.Header closeButton closeVariant="white" className="bg-gradient-to-r from-purple-600 to-pink-600 text-white border-0 py-3 px-4 modal-header">
          <Modal.Title className="font-bold text-lg text-white d-flex align-items-center gap-2">
            <FaEye />
            Enrolled Users — {rosterCourse?.course?.name || rosterCourse?.course?.title}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="bg-gray-50 p-0">
          {rosterLoading ? (
            <div className="text-center py-12">
              <Spinner animation="border" className="text-purple-600" />
            </div>
          ) : (
            <Tabs
              activeKey={rosterTab}
              onSelect={(key) => setRosterTab(key || 'learners')}
              className="px-3 pt-3 border-bottom border-purple-100"
            >
              <Tab
                eventKey="learners"
                title={
                  <span className="d-flex align-items-center gap-2 text-purple-700 fw-semibold">
                    <FaUserGraduate />
                    Learners
                    <Badge bg="light" text="dark" className="border border-purple-200">{enrolledLearners.length}</Badge>
                  </span>
                }
              >
                <div className="p-3 sm:p-4">
                  {enrolledLearners.length === 0 ? (
                    <p className="text-center text-sm text-purple-700 py-6 mb-0">
                      No learners from this organization are enrolled in this course.
                    </p>
                  ) : (
                    <div className="rounded-xl overflow-hidden border border-purple-100 shadow-sm bg-white">
                      <table className="m-0 w-full text-xs sm:text-sm">
                        <thead className="bg-gradient-to-r from-purple-600 to-pink-600 text-white">
                          <tr>
                            <th className="py-2 px-3 text-left font-bold">Name</th>
                            <th className="py-2 px-3 text-left font-bold">Email</th>
                            <th className="py-2 px-3 text-left font-bold">Enrolled</th>
                            <th className="py-2 px-3 text-left font-bold">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {enrolledLearners.map((enrollment) => {
                            const user = enrollment.User || {};
                            const loadingKey = `learner-${enrollment.id}`;
                            return (
                              <tr key={enrollment.id} className="border-b border-purple-100 hover:bg-purple-50/50 transition-all">
                                <td className="py-2 px-3 text-gray-800">{user.first_name} {user.last_name}</td>
                                <td className="py-2 px-3 text-gray-600">{user.email}</td>
                                <td className="py-2 px-3 text-gray-600">
                                  {enrollment.enrollment_date
                                    ? new Date(enrollment.enrollment_date).toLocaleDateString()
                                    : '-'}
                                </td>
                                <td className="py-2 px-3">
                                  <button
                                    type="button"
                                    onClick={() => handleDeEnrollLearner(enrollment)}
                                    disabled={rosterActionLoading === loadingKey}
                                    className="p-1 border-0 text-red-600 hover:bg-red-50 rounded transition disabled:opacity-50"
                                    title="Unenroll learner"
                                    aria-label={`Unenroll ${user.first_name} ${user.last_name}`}
                                  >
                                    {rosterActionLoading === loadingKey ? (
                                      <Spinner animation="border" size="sm" />
                                    ) : (
                                      <FaTrash className="w-4 h-4" />
                                    )}
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </Tab>
              <Tab
                eventKey="instructors"
                title={
                  <span className="d-flex align-items-center gap-2 text-purple-700 fw-semibold">
                    <FaChalkboardTeacher />
                    Instructors
                    <Badge bg="light" text="dark" className="border border-purple-200">{assignedInstructors.length}</Badge>
                  </span>
                }
              >
                <div className="p-3 sm:p-4">
                  {assignedInstructors.length === 0 ? (
                    <p className="text-center text-sm text-purple-700 py-6 mb-0">
                      No instructors from this organization are assigned to this course.
                    </p>
                  ) : (
                    <div className="rounded-xl overflow-hidden border border-purple-100 shadow-sm bg-white">
                      <table className="m-0 w-full text-xs sm:text-sm">
                        <thead className="bg-gradient-to-r from-purple-600 to-pink-600 text-white">
                          <tr>
                            <th className="py-2 px-3 text-left font-bold">Name</th>
                            <th className="py-2 px-3 text-left font-bold">Email</th>
                            <th className="py-2 px-3 text-left font-bold">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {assignedInstructors.map((instructor) => {
                            const loadingKey = `instructor-${instructor.id}`;
                            return (
                              <tr key={instructor.id} className="border-b border-purple-100 hover:bg-purple-50/50 transition-all">
                                <td className="py-2 px-3 text-gray-800">{instructor.first_name} {instructor.last_name}</td>
                                <td className="py-2 px-3 text-gray-600">{instructor.email}</td>
                                <td className="py-2 px-3">
                                  <button
                                    type="button"
                                    onClick={() => handleRemoveInstructor(instructor)}
                                    disabled={rosterActionLoading === loadingKey}
                                    className="p-1 border-0 text-red-600 hover:bg-red-50 rounded transition disabled:opacity-50"
                                    title="Remove instructor"
                                    aria-label={`Remove instructor ${instructor.first_name} ${instructor.last_name}`}
                                  >
                                    {rosterActionLoading === loadingKey ? (
                                      <Spinner animation="border" size="sm" />
                                    ) : (
                                      <FaTrash className="w-4 h-4" />
                                    )}
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </Tab>
            </Tabs>
          )}
        </Modal.Body>
        <Modal.Footer className="bg-gray-100 border-0 rounded-b-2xl p-4">
          <Row className="w-100 g-3">
            <Col md={12}>
              <Button
                bsPrefix="custom-hamburger"
                onClick={handleCloseRosterModal}
                disabled={!!rosterActionLoading}
                className="w-full border-1 bg-gray-100 hover:bg-gray-200 text-gray-700 border-2 border-gray-300 hover:border-gray-500 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2"
              >
                Close
              </Button>
            </Col>
          </Row>
        </Modal.Footer>
      </Modal>

      {/* ── Assign Instructor Modal ── */}
      <Modal
        show={showInstructorModal}
        onHide={() => !instructorSaving && setShowInstructorModal(false)}
        centered
        size="lg"
        backdrop="static"
      >
        <Modal.Header
          closeButton
          closeVariant="white"
          className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white border-0 py-3 px-4 modal-header"
        >
          <Modal.Title className="font-bold text-lg text-white flex items-center gap-2">
            <FaChalkboardTeacher className="inline" />
            Assign Instructors — {instructorCourse?.course?.name || instructorCourse?.course?.title}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="p-0 bg-gray-50">
          {instructorModalLoading ? (
            <div className="flex justify-center items-center py-16">
              <Spinner animation="border" className="text-indigo-600" />
            </div>
          ) : (
            <>
              {/* Search bar */}
              <div className="p-3 border-b border-indigo-100 bg-white">
                <div className="flex shadow-sm rounded-lg overflow-hidden bg-white border border-indigo-200">
                  <div className="flex items-center px-3 bg-white">
                    <FaSearch className="w-4 h-4 text-indigo-500" />
                  </div>
                  <input
                    type="text"
                    placeholder="Search instructors by name or email…"
                    value={instructorSearch}
                    onChange={(e) => setInstructorSearch(e.target.value)}
                    className="w-full px-3 py-2 border-0 outline-none text-sm"
                  />
                </div>
              </div>

              {/* Summary badges */}
              <div className="flex gap-2 px-4 pt-3 pb-1">
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium">
                  {assignedInstructorIds.length} assigned
                </span>
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs font-medium">
                  {allInstructors.length - assignedInstructorIds.length} unassigned
                </span>
              </div>

              {filteredInstructorList.length === 0 ? (
                <div className="text-center py-10 text-gray-500 text-sm">
                  {allInstructors.length === 0
                    ? 'No platform-level instructors found.'
                    : 'No instructors match your search.'}
                </div>
              ) : (
                <div className="p-3 grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-96 overflow-y-auto">
                  {filteredInstructorList.map((inst) => {
                    const isAssigned = assignedInstructorIds.includes(inst.id);
                    return (
                      <div
                        key={inst.id}
                        role="button"
                        tabIndex={0}
                        onClick={() => handleToggleInstructor(inst.id)}
                        onKeyDown={(e) => e.key === 'Enter' && handleToggleInstructor(inst.id)}
                        aria-label={`Toggle instructor ${inst.first_name} ${inst.last_name}`}
                        className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all duration-150 select-none
                          ${isAssigned
                            ? 'border-indigo-500 bg-indigo-50 shadow-sm'
                            : 'border-gray-200 bg-white hover:border-indigo-300 hover:bg-indigo-50/40'
                          }`}
                      >
                        <div className={`w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0
                          ${isAssigned ? 'bg-gradient-to-br from-indigo-500 to-purple-600' : 'bg-gradient-to-br from-gray-400 to-gray-500'}`}>
                          {inst.first_name?.charAt(0)?.toUpperCase() || '?'}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs sm:text-sm font-semibold text-gray-800 truncate">
                            {inst.first_name} {inst.last_name}
                          </p>
                          <p className="text-xs text-gray-500 truncate">{inst.email}</p>
                        </div>
                        <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0
                          ${isAssigned ? 'bg-indigo-500 border-indigo-500' : 'border-gray-300 bg-white'}`}>
                          {isAssigned && (
                            <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </Modal.Body>

        <Modal.Footer className="bg-gray-100 border-0 p-3">
          <Row className="w-100 g-2">
            <Col md={6}>
              <Button
                bsPrefix="custom-hamburger"
                onClick={() => setShowInstructorModal(false)}
                disabled={instructorSaving}
                className="w-full border-1 bg-gray-100 hover:bg-gray-200 text-gray-700 border-2 border-gray-300 hover:border-gray-500 font-semibold rounded-lg shadow-md transition-all text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2"
              >
                Cancel
              </Button>
            </Col>
            <Col md={6}>
              <Button
                bsPrefix="custom-hamburger"
                onClick={handleSaveInstructors}
                disabled={instructorSaving || instructorModalLoading}
                className="w-full border-1 border-indigo-600 bg-white text-indigo-600 font-semibold rounded-lg shadow-md hover:shadow-lg transition-all text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2"
              >
                {instructorSaving ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-1" />
                    Saving…
                  </>
                ) : (
                  'Save Assignments'
                )}
              </Button>
            </Col>
          </Row>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default OrgCourses;